<template>
  <template v-if="!isCollapsed">
    <transition name="slide">
      <div class="filter-box">
        <div ref="htmlRef" class="filter-container">
          <DashboardDropdownFilterComplex
            :complexes="complexes"
            :selectedComplex="filters.current.complex"
            :optionsWithData="nullableSet(information?.optionsWithData?.complexes)"
            @selectionChanged="complexSelected"
          />
          <DashboardDropdownFilterBuilding
            :buildings="filteredBuildings"
            :selectedBuildings="filters.current.buildings"
            :presentAsMultiSelect="!!filters.current.complex"
            :optionsWithData="nullableSet(information?.optionsWithData?.constructions)"
            @selectionChanged="(buildings) => filters.$patch({ current: { buildings } })"
          />
          <DashboardDropdownFilterLevel
            :levels="allLevels"
            :selectedLevel="filters.current.level"
            :optionsWithData="nullableSet(information?.optionsWithData?.levels)"
            @selectionChanged="(level) => filters.$patch({ current: { level } })"
          />
          <DashboardDropdownFilterRoomType
            v-if="filterMap && filterMap.CO_CLASSES"
            :roomTypes="allRoomTypes"
            :selectedRoomTypes="roomTypeFiltersToRoomType(filters.current.roomTypes)"
            :optionsWithData="nullableSet(information?.optionsWithData?.coClasses)"
            @selectionChanged="
              (roomTypes) =>
                filters.$patch({
                  current: { roomTypes: roomTypes.map(({ coclass }) => ({ coclass })) }
                })
            "
          />
        </div>
        <div class="button-row">
          <ButtonOutline
            size="small"
            :buttonText="t('general__text__dashboard_reset_selections')"
            @onClicked="filters.reset"
          />
          <ButtonPrimary
            size="small"
            :disabled="filters.isCurrentFilterSubmitted"
            :buttonText="t('general__text__dashboard_update_selections')"
            @onClicked="submitFilters"
          />
        </div>
      </div>
    </transition>
  </template>
</template>
<script lang="ts" setup>
import {
  DashboardDropdownFilterBuilding,
  DashboardDropdownFilterComplex,
  DashboardDropdownFilterLevel,
  DashboardDropdownFilterRoomType
} from '../components/filters'
import { ComplexProperties, BuildingProperties, LevelProperties, RoomType } from '../../types'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import { ref, watch, computed, nextTick, toRefs, onMounted } from 'vue'
import { useStore } from 'vuex'
import { getLevelsv1 } from '@/services/floors.service'
import { nullableSet } from '@/utils/set'
import { useI18n } from 'vue-i18n'
import { COCLASSES, COCLASSES_AH } from '@/interfaces/coclass.interface'
import { InformationResponse } from '@/api-client/models/utilization/Contracts'
import { useRealm } from '@/composables/useRealm'
import { FeatureType, useFilters } from '@/composables/useFilters'
import { getRealmId } from '@/utils/realmId'
import { roomTypeFiltersToRoomType } from '@/components/types'

type Props = {
  title: string
  information: InformationResponse | undefined
  isCollapsed: boolean
}

const props = defineProps<Props>()
const emits = defineEmits(['submitForm'])
const vuex = useStore()

const filters = useFilters(FeatureType.IndoorClimate)
const { t } = useI18n()
const { settings } = toRefs(useRealm())
const filterSettings = computed(() => settings.value?.config?.utilization.filters)
const filterMap = computed(() =>
  filterSettings.value
    ? Object.fromEntries(filterSettings.value?.map((x) => [x.type, true]))
    : undefined
)
const htmlRef = ref<HTMLElement | null>(null)
const complexes = computed<ComplexProperties[]>(() =>
  vuex.getters.allComplexes.map((x) => x.properties)
)
const buildings = computed<BuildingProperties[]>(() =>
  vuex.getters.allBuildings.map((x) => x.properties)
)
const filteredBuildings = computed<BuildingProperties[]>(() => {
  if (!filters.current.complex) {
    return buildings.value
  }
  const id = filters.current.complex.belongsToId
  return buildings.value.filter((x) => x.complexId === id)
})
const allLevels = ref<LevelProperties[]>([])
const allRoomTypes = ref<RoomType[]>(getRealmId() === 'akademiskahus' ? COCLASSES_AH : COCLASSES)

function submitFilters() {
  filters.submit()
  emits('submitForm')
}

watch(
  () => props.isCollapsed,
  async () => {
    await nextTick()
    htmlRef.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }
)

watch(
  () => filters.current.buildings,
  (values) => {
    updateLevelSelection(values)
    if (!filters.current.complex && values.length === 1) {
      updateComplexSelection(values)
    }
  }
)

onMounted(() => {
  if (filters.current.buildings.length === 1) {
    updateLevelSelection(filters.current.buildings)
    if (!filters.current.complex) {
      updateComplexSelection(filters.current.buildings)
    }
  }
})

const updateLevelSelection = async (buildings: BuildingProperties[]): Promise<void> => {
  if (buildings.length === 0) {
    filters.$patch({ current: { level: undefined } })
    return
  }
  if (buildings.length > 1) {
    allLevels.value = []
    filters.$patch({ current: { level: undefined } })
    return
  }
  try {
    allLevels.value = await getLevelsv1(buildings[0].id)
  } catch {
    allLevels.value = []
  }
}

function complexSelected(complex: ComplexProperties | undefined) {
  filters.$patch({
    current: {
      complex: complex,
      buildings: [],
      level: undefined
    }
  })
}

function updateComplexSelection(buildings: BuildingProperties[]) {
  const complex = complexes.value.find((x) => x.belongsToId === buildings[0].complexId)
  filters.$patch({ current: { complex: complex } })
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.filter-box {
  background-color: white;
  margin-left: -24px;
  margin-right: -24px;
  padding-left: 24px;
  padding-right: 24px;
  border-bottom: 1px solid #e0e0e0;
}
.filter-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: variables.$gap-large;
  padding: 16px 0;
}
.button-row {
  display: flex;
  gap: variables.$gap-large;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding-bottom: 16px;
}
// Sliding animation
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease-in-out;
}

.slide-enter,
.slide-leave-to {
  transform: translateY(-100%);
  opacity: 1;
}
</style>
